import { useState, useEffect } from 'react'
import { employeesAPI } from '../../services/api'
import { 
  UserGroupIcon, 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { t, locale } from '../../i18n'

function EmployeesPage() {
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    role: 'cashier',
    pin: '',
    salary: '',
    phone: '',
    email: '',
    hire_date: new Date().toISOString().split('T')[0],
    is_active: true
  })

  useEffect(() => {
    loadEmployees()
  }, [])

  const loadEmployees = async () => {
    try {
      setLoading(true)
      const response = await employeesAPI.getEmployees()
      if (response.data.success) {
        setEmployees(response.data.employees)
      }
    } catch (error) {
      toast.error(t("Failed to load employees"))
      console.error('Load employees error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee)
      setFormData({
        username: employee.username,
        password: '',
        full_name: employee.full_name,
        role: employee.role,
        pin: '',
        salary: employee.salary || '',
        phone: employee.phone || '',
        email: employee.email || '',
        hire_date: employee.hire_date || new Date().toISOString().split('T')[0],
        is_active: employee.is_active
      })
    } else {
      setEditingEmployee(null)
      setFormData({
        username: '',
        password: '',
        full_name: '',
        role: 'cashier',
        pin: '',
        salary: '',
        phone: '',
        email: '',
        hire_date: new Date().toISOString().split('T')[0],
        is_active: true
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingEmployee(null)
    setFormData({
      username: '',
      password: '',
      full_name: '',
      role: 'cashier',
      pin: '',
      salary: '',
      phone: '',
      email: '',
      hire_date: new Date().toISOString().split('T')[0],
      is_active: true
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.username.trim() || !formData.full_name.trim()) {
      toast.error(t("Please fill in all required fields"))
      return
    }

    if (!editingEmployee && !formData.password.trim()) {
      toast.error(t("Password is required for new employees"))
      return
    }

    try {
      setLoading(true)
      
      const submitData = { ...formData }
      if (editingEmployee && !submitData.password) {
        delete submitData.password
      }
      // PINs are stored hashed; blank keeps the current one.
      if (!submitData.pin) delete submitData.pin

      if (editingEmployee) {
        await employeesAPI.updateEmployee(editingEmployee.id, submitData)
        toast.success(t("Employee updated successfully"))
      } else {
        await employeesAPI.createEmployee(submitData)
        toast.success(t("Employee created successfully"))
      }

      await loadEmployees()
      handleCloseModal()
    } catch (error) {
      toast.error(t(error.response?.data?.error || 'Failed to save employee'))
      console.error('Save employee error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async (employee) => {
    const newStatus = !employee.is_active
    const action = newStatus ? 'activate' : 'deactivate'
    
    if (!window.confirm(t("Are you sure you want to {action} {full_name}?", { action: t(action), full_name: employee.full_name }))) {
      return
    }

    try {
      setLoading(true)
      const updateData = {
        ...employee,
        is_active: newStatus
      }
      await employeesAPI.updateEmployee(employee.id, updateData)
      toast.success(t(newStatus ? "Employee activated successfully" : "Employee deactivated successfully"))
      await loadEmployees()
    } catch (error) {
      toast.error(t(error.response?.data?.error || `Failed to ${action} employee`))
      console.error('Toggle status error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (employee) => {
    if (!window.confirm(t("Are you sure you want to permanently delete {full_name}? This action cannot be undone.", { full_name: employee.full_name }))) {
      return
    }

    try {
      setLoading(true)
      await employeesAPI.deleteEmployee(employee.id)
      toast.success(t("Employee deleted successfully"))
      await loadEmployees()
    } catch (error) {
      toast.error(t(error.response?.data?.error || 'Failed to delete employee'))
      console.error('Delete employee error:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.role.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesRole = filterRole === 'all' || emp.role === filterRole
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && emp.is_active) ||
                         (filterStatus === 'inactive' && !emp.is_active)
    return matchesSearch && matchesRole && matchesStatus
  })

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800 border-purple-200',
      manager: 'bg-blue-100 text-blue-800 border-blue-200',
      cashier: 'bg-green-100 text-green-800 border-green-200',
      kitchen: 'bg-orange-100 text-orange-800 border-orange-200',
      waiter: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    }
    return colors[role] || 'bg-gray-100 text-gray-800 border-gray-200'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold gradient-text">{t("Employee Management")}</h1>
          <p className="text-gray-600 mt-2">{t("Manage your team members and their roles")}</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 me-2" />
          {t("Add Employee")}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body text-center">
            <UserGroupIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">{employees.length}</div>
            <div className="text-sm text-gray-600">{t("Total Employees")}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              {employees.filter(e => e.is_active).length}
            </div>
            <div className="text-sm text-gray-600">{t("Active")}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <XCircleIcon className="h-8 w-8 text-red-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              {employees.filter(e => !e.is_active).length}
            </div>
            <div className="text-sm text-gray-600">{t("Inactive")}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-purple-600 font-bold text-lg">$</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {employees.filter(e => e.salary).length}
            </div>
            <div className="text-sm text-gray-600">{t("With Salary")}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute start-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t("Search by name, username, or role...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input ps-10"
              />
            </div>

            {/* Role Filter */}
            <div>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="form-input"
              >
                <option value="all">{t("All Roles")}</option>
                <option value="admin">{t("Admin")}</option>
                <option value="manager">{t("Manager")}</option>
                <option value="cashier">{t("Cashier")}</option>
                <option value="kitchen">{t("Kitchen")}</option>
                <option value="waiter">{t("Waiter")}</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="form-input"
              >
                <option value="all">{t("All Status")}</option>
                <option value="active">{t("Active")}</option>
                <option value="inactive">{t("Inactive")}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Employees Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Employee")}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Role")}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Contact")}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Salary")}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Status")}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading && filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="loading-spinner mx-auto mb-2"></div>
                      <p className="text-gray-600">{t("Loading employees...")}</p>
                    </td>
                  </tr>
                ) : filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600">{t("No employees found")}</p>
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((employee) => (
                    <tr key={employee.id} className={`hover:bg-gray-50 ${!employee.is_active ? 'bg-gray-50 opacity-75' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {employee.full_name}
                            {!employee.is_active && (
                              <span className="ms-2 text-xs text-gray-500">{t("(Inactive)")}</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">@{employee.username}</div>
                          {employee.has_pin && (
                            <div className="text-xs text-gray-400">{t("PIN set")}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge ${getRoleBadgeColor(employee.role)}`}>
                          {employee.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{employee.phone || '-'}</div>
                        <div className="text-sm text-gray-500">{employee.email || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {employee.salary ? t("{v0} MAD", { v0: parseFloat(employee.salary).toFixed(2) }) : '-'}
                        </div>
                        {employee.hire_date && (
                          <div className="text-xs text-gray-500">
                            {t("Since")} {new Date(employee.hire_date).toLocaleDateString(locale())}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {employee.is_active ? (
                          <span className="badge badge-success">{t("Active")}</span>
                        ) : (
                          <span className="badge badge-danger">{t("Inactive")}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleOpenModal(employee)}
                          className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                          title={t("Edit")}
                          disabled={loading}
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(employee)}
                          className={employee.is_active ? 
                            "text-orange-600 hover:text-orange-900 disabled:text-gray-400 disabled:cursor-not-allowed" : 
                            "text-green-600 hover:text-green-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                          }
                          title={employee.is_active ? t("Deactivate") : t("Activate")}
                          disabled={loading}
                        >
                          {employee.is_active ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                        <button
                          onClick={() => handleDelete(employee)}
                          className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                          title={t("Delete Permanently")}
                          disabled={loading}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingEmployee ? t("Edit Employee") : t("Add New Employee")}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Basic Information")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">{t("Full Name *")}</label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="form-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">{t("Username *")}</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="form-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">
                      {t("Password")} {!editingEmployee && '*'}
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="form-input"
                      placeholder={editingEmployee ? t("Leave blank to keep current") : ''}
                      required={!editingEmployee}
                    />
                  </div>

                  <div>
                    <label className="form-label">{t("Role *")}</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="form-input"
                      required
                    >
                      <option value="cashier">{t("Cashier")}</option>
                      <option value="waiter">{t("Waiter")}</option>
                      <option value="kitchen">{t("Kitchen")}</option>
                      <option value="manager">{t("Manager")}</option>
                      <option value="admin">{t("Admin")}</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">{t("PIN (4-6 digits)")}</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength="6"
                      value={formData.pin}
                      onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '') })}
                      className="form-input"
                      placeholder={editingEmployee?.has_pin ? t("Leave blank to keep current") : "1234"}
                    />
                  </div>

                  <div>
                    <label className="form-label">{t("Hire Date")}</label>
                    <input
                      type="date"
                      value={formData.hire_date}
                      onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Contact & Compensation */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Contact & Compensation")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">{t("Phone")}</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="form-input"
                      placeholder={t("+968 9XXX XXXX")}
                    />
                  </div>

                  <div>
                    <label className="form-label">{t("Email")}</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="form-input"
                      placeholder={t("employee@example.com")}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="form-label">{t("Monthly Salary (MAD)")}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.salary}
                      onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                      className="form-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              {editingEmployee && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Account Status")}</h3>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="sr-only"
                        />
                        <div className={`w-10 h-6 rounded-full transition-colors ${
                          formData.is_active ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <div className={`absolute start-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                          formData.is_active ? 'transform translate-x-4' : ''
                        }`}></div>
                      </div>
                      <span className="ms-3 text-sm font-medium text-gray-900">
                        {formData.is_active ? t("Active") : t("Inactive")}
                      </span>
                    </label>
                    <span className="text-sm text-gray-500">
                      {formData.is_active ? 
                        t("Employee can access the system") : 
                        t("Employee cannot access the system")
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-outline"
                  disabled={loading}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? t("Saving...") : editingEmployee ? t("Update Employee") : t("Add Employee")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmployeesPage